import { FiltersType } from '@/api/types';
import { client } from '@/client';
import {
  BulkLaunchCampaignsResponse,
  Campaign,
  CampaignRecommendationRequest,
  CampaignRecommendationResult,
  CampaignSummary,
  CloneCampaignRequest,
  CreateAutoPhishingCampaignRequest,
  CreateAutoPhishingCampaignsRequest,
  GetCampaignRequest,
  LanguageFailureNotice,
  LanguageGroupResult,
  ListCampaignsResponse,
  ListCampaignUserResultsResponse,
  MigrationCampaignRequest,
  PlaceholderLikelyPick,
  PlaceholderSelectionPreview,
  SaveCampaignRequest,
  SendTestEmail,
  SimulationsSummary,
  UpdateCampaignAction,
  UpdateCampaignRequest,
  UpdateLanguageGroupPayload,
} from '@/client/campaigns';
import { MessageResponseModel } from '@/client/models';
import {
  CloneCampaignMutation,
  DeleteCampaignMutation,
  GeneratePlaceholderLikelyPickMutation,
  GenericForbiddenApiError,
  SaveCampaignMutation,
  SendTestEmailCampaignMutation,
} from '@/hooks/query';
import { featuredQueryKey } from '@/hooks/query/campaign-templates.hook';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';

const queryKey = 'campaigns';
const singleQueryKey = 'campaign';
export const placeholderPreviewQueryKey = 'placeholder-selection-preview';

export const useCampaigns = (
  params: UseQueryOptions & {
    showIndividualCampaigns?: 0 | 1;
    withTemplates?: 0 | 1;
    withTargets?: 0 | 1;
    withDifficultyInMeta?: 0 | 1;
    take?: number;
    skip?: number;
    sort?: string[];
    filters?: FiltersType;
  },
): UseQueryResult<ListCampaignsResponse> & {
  campaigns: ListCampaignsResponse | undefined;
} => {
  const {
    take = 5,
    skip = 0,
    sort,
    filters,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [queryKey, take, skip, JSON.stringify(sort), JSON.stringify(filters)],
    () => client.campaigns.getCampaigns(params),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    campaigns: data,
    ...(rest as UseQueryResult<ListCampaignsResponse>),
  };
};

// Pages through every matching campaign. The planner groups a simulation's
// language sends into one row, so a page boundary must not split a group.
export const useAllCampaigns = (
  params: Parameters<typeof useCampaigns>[0],
): UseQueryResult<ListCampaignsResponse> & {
  campaigns: ListCampaignsResponse | undefined;
} => {
  const {
    take = 200,
    sort,
    filters,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [queryKey, 'all', take, JSON.stringify(sort), JSON.stringify(filters)],
    async () => {
      const first = await client.campaigns.getCampaigns({
        ...params,
        take,
        skip: 0,
      });
      const result = [...first.result];

      while (result.length < first.count) {
        const page = await client.campaigns.getCampaigns({
          ...params,
          take,
          skip: result.length,
        });
        if (page.result.length === 0) break;
        result.push(...page.result);
      }

      return { ...first, result };
    },
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    campaigns: data,
    ...(rest as UseQueryResult<ListCampaignsResponse>),
  };
};

export const useCampaignUserResults = (
  params: UseQueryOptions & {
    take?: number;
    skip?: number;
    sort?: string[];
    filters?: FiltersType;
  },
  campaignId?: string,
): UseQueryResult<ListCampaignUserResultsResponse> & {
  campaignUserResults: ListCampaignUserResultsResponse | undefined;
} => {
  const {
    take = 10,
    skip = 0,
    sort,
    filters,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [queryKey, take, skip, JSON.stringify(sort), JSON.stringify(filters)],
    () => client.campaigns.getCampaignUserResults(params, campaignId),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      keepPreviousData: true,
      ...(restOptions as any),
    },
  );

  return {
    campaignUserResults: data,
    ...(rest as UseQueryResult<ListCampaignUserResultsResponse>),
  };
};

export const useCampaignsSummary = (
  params: UseQueryOptions & {
    accountId?: string;
    showIndividualCampaigns?: 0 | 1;
  },
): UseQueryResult<CampaignSummary> & {
  campaignsSummary: CampaignSummary | undefined;
} => {
  const {
    accountId,
    showIndividualCampaigns,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [singleQueryKey, accountId, showIndividualCampaigns],
    () =>
      accountId
        ? client.campaigns.getCampaignsSummary(
            accountId,
            showIndividualCampaigns,
          )
        : undefined,
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      keepPreviousData: true,
      ...(restOptions as UseQueryResult<CampaignSummary>),
    },
  );

  return {
    campaignsSummary: data,
    ...(rest as UseQueryResult<CampaignSummary>),
  };
};

export const useCampaign = (
  campaignId?: string,
  params?: UseQueryOptions & GetCampaignRequest,
): UseQueryResult<Campaign> & {
  campaign: Campaign | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    filters,
    ...restOptions
  } = params || {};

  const { data, ...rest } = useQuery(
    [singleQueryKey, campaignId, JSON.stringify(filters)],
    () =>
      campaignId ? client.campaigns.getCampaign(campaignId, params) : undefined,
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as UseQueryResult<Campaign>),
    },
  );

  return {
    campaign: data,
    ...(rest as UseQueryResult<Campaign>),
  };
};

export const useSaveCampaign = (): {
  create: (payload: SaveCampaignRequest) => Promise<Campaign>;
} & SaveCampaignMutation => {
  const { mutateAsync, ...rest } = useMutation<
    Campaign,
    GenericForbiddenApiError,
    SaveCampaignRequest
  >('createCampaign', (payload: SaveCampaignRequest) =>
    client.campaigns.saveCampaign(payload),
  );

  return { create: mutateAsync, ...(rest as any) };
};

export const useCopyCampaign = (): {
  copy: (payload: CloneCampaignRequest) => Promise<Campaign>;
} & CloneCampaignMutation => {
  const { mutateAsync, ...rest } = useMutation<
    Campaign,
    GenericForbiddenApiError,
    CloneCampaignRequest
  >('copyCampaign', (payload: CloneCampaignRequest) =>
    client.campaigns.copyCampaign(payload),
  );

  return { copy: mutateAsync, ...(rest as any) };
};

export const useCloneCampaign = (): {
  clone: (payload: CloneCampaignRequest) => Promise<Campaign>;
} & CloneCampaignMutation => {
  const { mutateAsync, ...rest } = useMutation<
    Campaign,
    GenericForbiddenApiError,
    CloneCampaignRequest
  >('cloneCampaign', (payload: CloneCampaignRequest) =>
    client.campaigns.cloneCampaign(payload),
  );

  return { clone: mutateAsync, ...(rest as any) };
};

export const useCampaignAction = (): {
  updateStatus: (
    payload: UpdateCampaignAction,
  ) => Promise<MessageResponseModel>;
} & UseQueryResult => {
  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    UpdateCampaignAction
  >('updateCampaignStatus', (payload) =>
    client.campaigns.saveCampaignAction(payload),
  );

  return { updateStatus: mutateAsync, ...(rest as any) };
};

export const useSendTestEmail = (): {
  send: (
    payload: SendTestEmail,
  ) => Promise<{ jobId: string } & MessageResponseModel>;
} & SendTestEmailCampaignMutation => {
  const { mutateAsync, ...rest } = useMutation<
    { jobId: string } & MessageResponseModel,
    GenericForbiddenApiError,
    SendTestEmail
  >('sendTestEmailCampaign', (payload: SendTestEmail) =>
    client.campaigns.sendTestEmail(payload),
  );

  return { send: mutateAsync, ...(rest as any) };
};

export const useUpdateCampaign = (): {
  update: (payload: UpdateCampaignRequest) => Promise<Campaign>;
} & SaveCampaignMutation => {
  const { mutateAsync, ...rest } = useMutation<
    Campaign,
    GenericForbiddenApiError,
    UpdateCampaignRequest
  >('updateCampaign', (payload: UpdateCampaignRequest) =>
    client.campaigns.updateCampaign(payload),
  );

  return { update: mutateAsync, ...(rest as any) };
};

export const useDeleteCampaign = (): {
  delete: (campaignId: string) => Promise<MessageResponseModel>;
} & DeleteCampaignMutation => {
  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    string
  >('deleteCampaign', (campaignId: string) =>
    client.campaigns.deleteCampaign(campaignId),
  );

  return { delete: mutateAsync, ...(rest as any) };
};

export const useMigrateCampaign = (): {
  migrate: (payload: MigrationCampaignRequest) => Promise<MessageResponseModel>;
} => {
  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    MigrationCampaignRequest
  >('migrateCampaign', (payload: MigrationCampaignRequest) =>
    client.campaigns.migrateCampaign(payload),
  );

  return { migrate: mutateAsync, ...(rest as any) };
};

export const useCampaignRecommendation = (
  accountId: string,
  params: CampaignRecommendationRequest,
  options: UseQueryOptions,
): UseQueryResult<CampaignRecommendationResult> & {
  result: CampaignRecommendationResult | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    enabled = false,
    refetchOnWindowFocus = false,
    refetchOnMount = false,
    ...restOptions
  } = options;

  const { data, ...rest } = useQuery(
    [queryKey, accountId, params],
    () => client.campaigns.recommendCampaign(accountId, params),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      enabled,
      refetchOnWindowFocus,
      refetchOnMount,
      ...(restOptions as any),
    },
  );

  return {
    result: data,
    ...(rest as UseQueryResult<CampaignRecommendationResult>),
  };
};

export const useAutoPhishingSummary = (
  accountId?: string,
  params?: UseQueryOptions & {
    take?: number;
    skip?: number;
    sort?: string[];
    filters?: FiltersType;
  },
): UseQueryResult<SimulationsSummary> & {
  autoPhishingSummary: SimulationsSummary | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    enabled = true,
    filters,
    ...restOptions
  } = params || {};

  const { data, ...rest } = useQuery(
    ['phishing-summary', accountId, JSON.stringify(filters)],
    () =>
      accountId
        ? client.campaigns.getSimulationsSummary(accountId, params || {})
        : undefined,
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      enabled: enabled && !!accountId,
      ...(restOptions as any),
    },
  );

  return {
    autoPhishingSummary: data,
    ...(rest as UseQueryResult<SimulationsSummary>),
  };
};

export const useCreateAutoPhishingCampaign = (): {
  create: (
    accountId: string,
    payload: CreateAutoPhishingCampaignRequest,
  ) => Promise<Campaign>;
} & SaveCampaignMutation => {
  const queryClient = useQueryClient();

  const { mutateAsync, ...rest } = useMutation<
    Campaign,
    GenericForbiddenApiError,
    { accountId: string; payload: CreateAutoPhishingCampaignRequest }
  >(
    'createAutoPhishingCampaign',
    ({ accountId, payload }) =>
      client.campaigns.createAutoPhishingCampaign(accountId, payload),
    {
      onSuccess: (_result, { accountId }) => {
        queryClient.invalidateQueries([featuredQueryKey, accountId]);
      },
    },
  );

  return {
    create: (accountId: string, payload: CreateAutoPhishingCampaignRequest) =>
      mutateAsync({ accountId, payload }),
    ...(rest as any),
  };
};

export const useLaunchAutoPhishingCampaigns = (): {
  launch: (
    accountId: string,
    campaignIds: string[],
  ) => Promise<BulkLaunchCampaignsResponse>;
} & UseQueryResult => {
  const { mutateAsync, ...rest } = useMutation<
    BulkLaunchCampaignsResponse,
    GenericForbiddenApiError,
    { accountId: string; campaignIds: string[] }
  >('launchAutoPhishingCampaigns', ({ accountId, campaignIds }) =>
    client.campaigns.launchAutoPhishingCampaigns(accountId, campaignIds),
  );

  return {
    launch: (accountId: string, campaignIds: string[]) =>
      mutateAsync({ accountId, campaignIds }),
    ...(rest as any),
  };
};

export const useCreateAutoPhishingCampaigns = (): {
  create: (
    accountId: string,
    payload: CreateAutoPhishingCampaignsRequest,
  ) => Promise<{ message: string; jobId: string } & MessageResponseModel>;
} & UseQueryResult => {
  const { mutateAsync, ...rest } = useMutation<
    { message: string; jobId: string } & MessageResponseModel,
    GenericForbiddenApiError,
    { accountId: string; payload: CreateAutoPhishingCampaignsRequest }
  >('createAutoPhishingCampaigns', ({ accountId, payload }) =>
    client.campaigns.createAutoPhishingCampaigns(accountId, payload),
  );

  return {
    create: (accountId: string, payload: CreateAutoPhishingCampaignsRequest) =>
      mutateAsync({ accountId, payload }),
    ...(rest as any),
  };
};

export const usePlaceholderSelectionPreview = (
  accountId?: string,
  params?: UseQueryOptions,
): UseQueryResult<PlaceholderSelectionPreview[]> & {
  placeholderSelectionPreview: PlaceholderSelectionPreview[] | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    enabled = false,
    refetchOnWindowFocus = false,
    refetchOnMount = false,
    ...restOptions
  } = params || {};

  const { data, ...rest } = useQuery(
    [placeholderPreviewQueryKey, accountId],
    () =>
      accountId
        ? client.campaigns.getPlaceholderSelectionPreview(accountId)
        : undefined,
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      enabled: enabled && !!accountId,
      refetchOnWindowFocus,
      refetchOnMount,
      ...(restOptions as any),
    },
  );

  return {
    placeholderSelectionPreview: data,
    ...(rest as UseQueryResult<PlaceholderSelectionPreview[]>),
  };
};

export const useGeneratePlaceholderLikelyPick = (): {
  generate: (
    accountId: string,
    placeholderId: string,
  ) => Promise<PlaceholderLikelyPick>;
} & Omit<GeneratePlaceholderLikelyPickMutation, 'mutateAsync'> => {
  const { mutateAsync, ...rest } = useMutation<
    PlaceholderLikelyPick,
    GenericForbiddenApiError,
    { accountId: string; placeholderId: string }
  >(
    'generatePlaceholderLikelyPick',
    ({ accountId, placeholderId }) =>
      client.campaigns.generatePlaceholderLikelyPick(accountId, placeholderId),
    { retry: false },
  );

  return {
    generate: (accountId: string, placeholderId: string) =>
      mutateAsync({ accountId, placeholderId }),
    ...rest,
  };
};

const invalidateCampaignQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  queryClient.invalidateQueries([queryKey]);
  queryClient.invalidateQueries(['phishing-summary']);
};

export const useUpdateLanguageGroup = (): {
  updateLanguageGroup: (
    accountId: string,
    languageGroupId: string,
    payload: UpdateLanguageGroupPayload,
  ) => Promise<LanguageGroupResult>;
} & UseQueryResult => {
  const queryClient = useQueryClient();

  const { mutateAsync, ...rest } = useMutation<
    LanguageGroupResult,
    GenericForbiddenApiError,
    {
      accountId: string;
      languageGroupId: string;
      payload: UpdateLanguageGroupPayload;
    }
  >(
    'updateLanguageGroup',
    ({ accountId, languageGroupId, payload }) =>
      client.campaigns.updateLanguageGroup(accountId, languageGroupId, payload),
    { onSuccess: () => invalidateCampaignQueries(queryClient) },
  );

  return {
    updateLanguageGroup: (accountId, languageGroupId, payload) =>
      mutateAsync({ accountId, languageGroupId, payload }),
    ...(rest as any),
  };
};

export const useDeleteLanguageGroup = (): {
  deleteLanguageGroup: (
    accountId: string,
    languageGroupId: string,
  ) => Promise<LanguageGroupResult>;
} & UseQueryResult => {
  const queryClient = useQueryClient();

  const { mutateAsync, ...rest } = useMutation<
    LanguageGroupResult,
    GenericForbiddenApiError,
    { accountId: string; languageGroupId: string }
  >(
    'deleteLanguageGroup',
    ({ accountId, languageGroupId }) =>
      client.campaigns.deleteLanguageGroup(accountId, languageGroupId),
    { onSuccess: () => invalidateCampaignQueries(queryClient) },
  );

  return {
    deleteLanguageGroup: (accountId, languageGroupId) =>
      mutateAsync({ accountId, languageGroupId }),
    ...(rest as any),
  };
};

export const useLanguageFailureNotices = (
  accountId?: string,
  enabled = true,
): UseQueryResult<LanguageFailureNotice[]> & {
  notices: LanguageFailureNotice[] | undefined;
} => {
  const { data, ...rest } = useQuery(
    ['language-failure-notices', accountId],
    () => client.campaigns.getLanguageFailureNotices(accountId as string),
    {
      enabled: enabled && !!accountId,
      staleTime: Infinity,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
    },
  );

  return {
    notices: data,
    ...(rest as UseQueryResult<LanguageFailureNotice[]>),
  };
};
